import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Shield,
  UserCheck,
  Car,
  CheckCircle,
  Plane,
  Luggage,
  User,
  CreditCard,
  Repeat,
  ArrowRight,
  CalendarIcon,
  MapPin,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PaymentMethodCards from "@/components/PaymentMethodCards";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useSeo } from "@/hooks/useSeo";

const includedServices = [
  { icon: Shield, label: "Free Cancellation" },
  { icon: UserCheck, label: "Meet & Greet" },
  { icon: Car, label: "Private Transfer" },
  { icon: CheckCircle, label: "Licensed Chauffeurs" },
];

interface BookingState {
  from: string;
  to: string;
  fromPlaceId?: string;
  toPlaceId?: string;
  routeType: string;
  passengers: number;
  promoCode?: string;
  distanceKm: number | null;
  durationMin: number | null;
  vehicle: {
    id: string;
    name: string;
    image_url: string | null;
    price: number;
    capacity: number;
  };
}

const timeSlots = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? "00" : "30";
  return `${h.toString().padStart(2, "0")}:${m}`;
});

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
};

const BookingPage = () => {
  useSeo({ robots: "noindex,follow", title: "Complete Booking | Allen Travels RD" });
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as BookingState | null;

  const [arrivalDate, setArrivalDate] = useState<Date>();
  const [arrivalTime, setArrivalTime] = useState("");
  const [arrivalAirline, setArrivalAirline] = useState("");
  const [arrivalFlightNumber, setArrivalFlightNumber] = useState("");

  const [returnDate, setReturnDate] = useState<Date>();
  const [returnTime, setReturnTime] = useState("");
  const [returnAirline, setReturnAirline] = useState("");
  const [returnFlightNumber, setReturnFlightNumber] = useState("");

  const [luggage, setLuggage] = useState("2");
  const [remarks, setRemarks] = useState("");

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [paymentMethod, setPaymentMethod] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeMethods, setActiveMethods] = useState<{ method: string; is_active: boolean }[]>([]);

  useEffect(() => {
    supabase
      .from("payment_methods")
      .select("method, is_active")
      .eq("is_active", true)
      .then(({ data }) => {
        const methods = (data as any[]) ?? [];
        setActiveMethods(methods);
        if (methods.length > 0 && !paymentMethod) {
          setPaymentMethod(methods[0].method);
        }
      });
  }, []);

  if (!state) {
    return (
      <div className="min-h-screen bg-gradient-soft flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center space-y-5 max-w-md">
            <div className="w-16 h-16 rounded-2xl bg-gradient-ocean mx-auto flex items-center justify-center">
              <Car className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-display text-2xl font-semibold text-foreground">
              No transfer in progress
            </h1>
            <p className="text-muted-foreground">Pick a route first and we'll guide you through.</p>
            <Button size="lg" onClick={() => navigate("/")} className="bg-gradient-ocean">
              Go Home
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const { from, to, fromPlaceId, toPlaceId, routeType, passengers, vehicle, distanceKm, durationMin } = state;

  const isFromAirport = fromPlaceId?.startsWith("db-airport-");
  const isToAirport = toPlaceId?.startsWith("db-airport-");
  const showArrivalFlightFields = !!isFromAirport;
  const showReturnSection = routeType === "round-trip";
  const showReturnFlightFields = !!(isFromAirport || isToAirport);

  const handleReserve = async () => {
    if (!arrivalDate || !arrivalTime || !fullName || !email || !phone) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (showReturnSection && (!returnDate || !returnTime)) {
      toast.error("Please fill in return date and time.");
      return;
    }

    setSubmitting(true);

    const [h, m] = arrivalTime.split(":").map(Number);
    const pickupDateTime = new Date(Date.UTC(arrivalDate.getFullYear(), arrivalDate.getMonth(), arrivalDate.getDate(), h, m, 0, 0));

    let returnDateTime: Date | null = null;
    if (showReturnSection && returnDate && returnTime) {
      const [rh, rm] = returnTime.split(":").map(Number);
      returnDateTime = new Date(Date.UTC(returnDate.getFullYear(), returnDate.getMonth(), returnDate.getDate(), rh, rm, 0, 0));
    }

    const flightNum = [
      arrivalFlightNumber,
      returnFlightNumber ? `Return: ${returnFlightNumber}` : null,
    ].filter(Boolean).join(" | ") || null;

    const { data: booking, error } = await supabase.from("bookings").insert({
      pickup_location: from,
      dropoff_location: to,
      route_type: routeType,
      passengers,
      pickup_date: pickupDateTime.toISOString(),
      return_date: returnDateTime?.toISOString() || null,
      flight_number: flightNum,
      customer_name: fullName,
      customer_email: email,
      customer_phone: phone,
      vehicle_id: vehicle.id,
      total_price: vehicle.price,
      special_requests: [
        `Luggage: ${luggage}`,
        arrivalAirline ? `Airline: ${arrivalAirline}` : null,
        returnAirline ? `Return Airline: ${returnAirline}` : null,
        remarks || null,
      ].filter(Boolean).join(" | ") || null,
      distance_km: distanceKm,
      estimated_time_min: durationMin,
      payment_status: paymentMethod === "cash" ? "unpaid" : "pending",
      payment_method: paymentMethod,
    } as any).select().single();

    setSubmitting(false);

    if (error) {
      toast.error("Failed to create booking. Please try again.");
      console.error(error);
    } else if (paymentMethod === "paypal") {
      navigate("/payment", {
        state: {
          bookingId: booking.id,
          bookingCode: booking.booking_code,
          from,
          to,
          passengers,
          routeType,
          totalPrice: vehicle.price,
          pickupDate: booking.pickup_date,
          returnDate: booking.return_date,
          distanceKm: booking.distance_km,
          estimatedTimeMin: booking.estimated_time_min,
          customerName: booking.customer_name,
          customerEmail: booking.customer_email,
          customerPhone: booking.customer_phone,
          flightNumber: booking.flight_number,
          specialRequests: booking.special_requests,
          paymentMethod: booking.payment_method,
          vehicle: { name: vehicle.name, image_url: vehicle.image_url },
        },
      });
    } else {
      supabase.functions.invoke("send-booking-email", { body: { booking_id: booking.id } })
        .catch((err) => console.error("Email send error:", err));
      toast.success("Booking confirmed!");
      navigate("/confirmation", {
        state: { booking, vehicle: { name: vehicle.name, image_url: vehicle.image_url } },
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-soft flex flex-col">
      <Navbar />

      {/* Hero strip */}
      <section className="relative pt-28 pb-10 overflow-hidden isolate">
        <div className="absolute inset-0 z-0 bg-gradient-hero" />
        <div className="absolute inset-0 z-0 opacity-30 mix-blend-overlay bg-[radial-gradient(ellipse_at_top,white,transparent_60%)]" />
        <div className="relative z-10 container mx-auto px-4 text-white">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors mb-5"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-dark text-xs font-medium tracking-[0.22em] uppercase">
            <Sparkles className="w-3.5 h-3.5 text-accent" /> Step 3 of 3
          </span>
          <h1 className="font-display text-3xl md:text-5xl font-semibold mt-4 leading-tight">
            Almost there — <span className="text-gradient-sunset">your details</span>
          </h1>
          <p className="mt-3 text-white/75 max-w-xl">
            Tell us about your flight and we'll be tracking it. Your driver will be waiting.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 pb-20 -mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT: Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* A) Flight / Schedule */}
            <motion.div {...fadeUp} className="p-7 rounded-2xl glass shadow-card space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-ocean flex items-center justify-center shadow-glow">
                  <Plane className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold text-foreground">
                    {showArrivalFlightFields ? "Flight Information" : "Schedule"}
                  </h2>
                  <p className="text-xs text-muted-foreground">When should we pick you up?</p>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-[11px] font-semibold text-primary uppercase tracking-[0.22em]">
                  {showArrivalFlightFields ? "Arrival Flight" : "Pickup Details"}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn("w-full justify-start text-left font-normal h-11 bg-background/70", !arrivalDate && "text-muted-foreground")}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {arrivalDate ? format(arrivalDate, "PPP") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={arrivalDate} onSelect={setArrivalDate} disabled={(d) => d < new Date()} initialFocus className="p-3 pointer-events-auto" />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Time *</Label>
                    <Select value={arrivalTime} onValueChange={setArrivalTime}>
                      <SelectTrigger className="h-11 bg-background/70"><SelectValue placeholder="Select time" /></SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {showArrivalFlightFields && (
                    <>
                      <div className="space-y-2">
                        <Label>Airline</Label>
                        <Input placeholder="e.g. JetBlue" value={arrivalAirline} onChange={(e) => setArrivalAirline(e.target.value)} className="h-11 bg-background/70" />
                      </div>
                      <div className="space-y-2">
                        <Label>Flight Number</Label>
                        <Input placeholder="e.g. B6 1234" value={arrivalFlightNumber} onChange={(e) => setArrivalFlightNumber(e.target.value)} className="h-11 bg-background/70" />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {showReturnSection && (
                <div className="space-y-4 pt-5 border-t border-border/60">
                  <p className="text-[11px] font-semibold text-primary uppercase tracking-[0.22em]">
                    {showReturnFlightFields ? "Return Flight" : "Return Details"}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Return Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className={cn("w-full justify-start text-left font-normal h-11 bg-background/70", !returnDate && "text-muted-foreground")}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {returnDate ? format(returnDate, "PPP") : "Select date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={returnDate} onSelect={setReturnDate} disabled={(d) => d < new Date()} initialFocus className="p-3 pointer-events-auto" />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>Return Time *</Label>
                      <Select value={returnTime} onValueChange={setReturnTime}>
                        <SelectTrigger className="h-11 bg-background/70"><SelectValue placeholder="Select time" /></SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((t) => <SelectItem key={`r-${t}`} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {showReturnFlightFields && (
                      <>
                        <div className="space-y-2">
                          <Label>Return Airline</Label>
                          <Input placeholder="e.g. American Airlines" value={returnAirline} onChange={(e) => setReturnAirline(e.target.value)} className="h-11 bg-background/70" />
                        </div>
                        <div className="space-y-2">
                          <Label>Return Flight Number</Label>
                          <Input placeholder="e.g. AA 5678" value={returnFlightNumber} onChange={(e) => setReturnFlightNumber(e.target.value)} className="h-11 bg-background/70" />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </motion.div>

            {/* B) Extra info */}
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="p-7 rounded-2xl glass shadow-card space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-ocean flex items-center justify-center shadow-glow">
                  <Luggage className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold text-foreground">Extra Information</h2>
                  <p className="text-xs text-muted-foreground">Anything we should know?</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount of Luggage</Label>
                  <Select value={luggage} onValueChange={setLuggage}>
                    <SelectTrigger className="h-11 bg-background/70"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 20 }, (_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Remarks / Special Instructions</Label>
                <Textarea
                  placeholder="Child seat needed, wheelchair accessible, etc."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="bg-background/70 min-h-[100px]"
                />
              </div>
            </motion.div>

            {/* C) Personal info */}
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.16 }} className="p-7 rounded-2xl glass shadow-card space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-ocean flex items-center justify-center shadow-glow">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold text-foreground">Personal Information</h2>
                  <p className="text-xs text-muted-foreground">We'll send your voucher here</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Full Name *</Label>
                  <Input placeholder="John Doe" value={fullName} onChange={(e) => setFullName(e.target.value)} className="h-11 bg-background/70" />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input type="email" placeholder="john@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-background/70" />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input type="tel" placeholder="+1 809 555 1234" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-11 bg-background/70" />
                </div>
              </div>
            </motion.div>

            {/* D) Payment */}
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.24 }} className="p-7 rounded-2xl glass shadow-card space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-sunset flex items-center justify-center shadow-glow">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold text-foreground">Payment Method</h2>
                  <p className="text-xs text-muted-foreground">Pay now or to your driver</p>
                </div>
              </div>
              <PaymentMethodCards
                value={paymentMethod}
                onChange={setPaymentMethod}
                activeMethods={activeMethods}
                cashSubtitle="Pay your driver directly"
              />

              <Button
                size="lg"
                className="w-full font-semibold text-base mt-4 h-12 bg-gradient-ocean hover:opacity-95 shadow-glow"
                onClick={handleReserve}
                disabled={submitting}
              >
                {submitting ? "Processing…" : `Reserve Now · $${vehicle.price}`}
              </Button>

              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                Secure checkout · Free cancellation up to 24h before
              </div>
            </motion.div>
          </div>

          {/* RIGHT: Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="sticky top-28 rounded-2xl overflow-hidden glass shadow-elegant"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={vehicle.image_url || "/placeholder.svg"}
                  alt={vehicle.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--hero-bg))]/85 via-transparent to-transparent" />
                <div className="absolute top-3 right-3">
                  <Badge variant="secondary" className="text-xs gap-1.5 bg-white/90 backdrop-blur">
                    {routeType === "round-trip" ? (
                      <><Repeat className="w-3 h-3" /> Round Trip</>
                    ) : (
                      <><ArrowRight className="w-3 h-3" /> One Way</>
                    )}
                  </Badge>
                </div>
                <div className="absolute bottom-3 left-4 right-4 text-white">
                  <h4 className="font-display font-semibold text-lg leading-tight">{vehicle.name}</h4>
                  <p className="text-xs text-white/80 mt-0.5">Up to {vehicle.capacity} passengers</p>
                </div>
              </div>

              <div className="p-6 space-y-5">
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2.5">
                    <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em] font-semibold">Pick-up</p>
                      <p className="text-foreground font-medium truncate">{from}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <div className="w-2 h-2 rounded-full bg-accent mt-1.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em] font-semibold">Drop-off</p>
                      <p className="text-foreground font-medium truncate">{to}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border/60">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Passengers</p>
                      <p className="text-foreground font-medium">{passengers}</p>
                    </div>
                    {distanceKm && (
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Distance</p>
                        <p className="text-foreground font-medium">{distanceKm} km</p>
                      </div>
                    )}
                    {durationMin && (
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Est. Time</p>
                        <p className="text-foreground font-medium">~{durationMin} min</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-5 border-t border-border/60">
                  <div className="flex items-end justify-between">
                    <p className="text-muted-foreground text-sm">Total</p>
                    <p className="text-3xl font-display font-semibold text-gradient-ocean">${vehicle.price}</p>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1 text-right">
                    {routeType === "round-trip" ? "round trip · taxes included" : "one way · taxes included"}
                  </p>
                </div>

                <div className="pt-4 border-t border-border/60">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em] mb-3 font-semibold">Included</p>
                  <div className="space-y-2">
                    {includedServices.map((s) => (
                      <div key={s.label} className="flex items-center gap-2 text-sm">
                        <s.icon className="w-3.5 h-3.5 text-primary" />
                        <span className="text-foreground">{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookingPage;
